/**
 * Safe Area Utility
 *
 * Handles iOS safe area insets for notch and home indicator
 * Uses CSS environment variables (safe-area-inset-*)
 */

export class SafeAreaUtil {
  private static cachedInsets: {
    top: number;
    bottom: number;
    left: number;
    right: number;
  } | null = null;

  /**
   * Detect if running on iOS
   */
  static isIOS(): boolean {
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;

    // Check for iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream;

    // Check for iOS Safari
    const isSafari = /Safari/.test(userAgent) && !/Chrome/.test(userAgent);

    return isIOSDevice || isSafari;
  }

  /**
   * Get safe area insets from CSS environment variables
   */
  static getSafeAreaInsets(): { top: number; bottom: number; left: number; right: number } {
    // Return cached values if available
    if (this.cachedInsets) {
      return this.cachedInsets;
    }

    // Create a temporary element to read CSS environment variables
    const testDiv = document.createElement('div');
    testDiv.style.position = 'fixed';
    testDiv.style.top = 'env(safe-area-inset-top, 0px)';
    testDiv.style.bottom = 'env(safe-area-inset-bottom, 0px)';
    testDiv.style.left = 'env(safe-area-inset-left, 0px)';
    testDiv.style.right = 'env(safe-area-inset-right, 0px)';
    testDiv.style.visibility = 'hidden';
    document.body.appendChild(testDiv);

    const computedStyle = window.getComputedStyle(testDiv);

    this.cachedInsets = {
      top: this.parsePx(computedStyle.top) || 0,
      bottom: this.parsePx(computedStyle.bottom) || 0,
      left: this.parsePx(computedStyle.left) || 0,
      right: this.parsePx(computedStyle.right) || 0,
    };

    document.body.removeChild(testDiv);

    // Fallback values for iOS if env() didn't work
    if (this.isIOS() && this.cachedInsets.top === 0) {
      // iPhone X and later have notches
      // Typical safe area insets: top 44-47px, bottom 34px
      const hasNotch = window.screen.height >= 812; // iPhone X and later

      if (hasNotch) {
        console.log('📱 iOS with notch detected - using fallback safe areas');
        this.cachedInsets.top = 44; // Status bar + notch
        this.cachedInsets.bottom = 34; // Home indicator
      } else {
        // Older iPhones
        this.cachedInsets.top = 20; // Status bar only
        this.cachedInsets.bottom = 0;
      }
    }

    console.log('📱 Safe Area Insets:', this.cachedInsets);
    return this.cachedInsets;
  }

  /**
   * Parse pixel value from CSS string
   */
  private static parsePx(value: string): number {
    if (!value || value === 'auto' || value === '0px') return 0;
    return parseFloat(value.replace('px', '')) || 0;
  }

  /**
   * Get top safe area padding
   */
  static getTopPadding(): number {
    return this.getSafeAreaInsets().top;
  }

  /**
   * Get bottom safe area padding
   */
  static getBottomPadding(): number {
    return this.getSafeAreaInsets().bottom;
  }

  /**
   * Clear cached insets (useful for orientation changes)
   */
  static clearCache(): void {
    this.cachedInsets = null;
  }
}
